// Central business configuration. Every public business fact lives here so it stays
// consistent across pages, FAQs, structured data and future quotation emails.
// Items marked `confirmed: false` are intentionally NOT published until Rekisa/owner confirms.

export const SITE = {
  name: "KhozaLand",
  legalName: "KhozaLand (Pty) Ltd",
  registrationNumber: "2017/434445/07",
  founder: "Yolanda Mketo",
  founderTitle: "Founder & CEO",
  foundingYear: 2020,
  motto: "POWER OF DREAMS.",
  tagline: "Your Style. Your Way.",
  ownership: "100% women-led",
  locality: "Mitchells Plain",
  city: "Cape Town",
  region: "Western Cape",
  country: "South Africa",
  countryCode: "ZA",
  locationLabel: "Mitchells Plain, Cape Town",
  phoneDisplay: "073 029 4047",
  phoneE164: "+27730294047",
  whatsappNumber: "27730294047",
  email: "yolanda@khozalandsa.co.za",
  social: [
    { id: "facebook", label: "Facebook", url: "https://www.facebook.com/people/KhozaLand/61589635659813/" },
    { id: "instagram", label: "Instagram", url: "https://www.instagram.com/khoza_land/" },
    { id: "tiktok", label: "TikTok", url: "https://www.tiktok.com/@khoza_land" },
  ],
  logo: "/images/khozaland-logo.png",
  logoWhite: "/images/khozaland-logo-white.png",
  ogImage: "/images/ingcawe-blanket-rhudulu-clan-lg.webp",
};

// Configurable order terms. Only `confirmed: true` values are shown as firm statements.
export const ORDER_TERMS = {
  turnaround: {
    confirmed: false,
    reported: "normally 3–5 working days, depending on the product and order",
  },
  urgentOrders: { confirmed: true, accepted: false },
  bulkDiscount: { confirmed: true, threshold: 10, text: "Discounts may be available for orders of 10 or more items, depending on the product." },
  deposit: { confirmed: false, percentage: null }, // conflicting 60% / 70% supplied - do not publish
  delivery: { confirmed: false, text: "Suitable orders outside Cape Town may qualify for courier delivery, including PAXI, subject to confirmation." },
  businessHours: { confirmed: false, text: null }, // conflicting schedules supplied - do not publish
};

// Brochure: set `url` once an approved public brochure exists. Until then the site shows
// "Request Our Product Catalogue" (lead capture) instead of a dead download link.
export const BROCHURE = { url: null, label: "Download Our Brochure" };

export const PRICE_NOTE =
  "Prices are from KhozaLand's current price list and include branding in your choice of style. Final pricing depends on quantity, design and finish, and is confirmed in your quotation.";

export const WHATSAPP_MESSAGES = {
  general: "Hello KhozaLand.\n\nI would like to enquire about a quotation.\n\nProduct:\nQuantity:\nDesign requirements:",
  tshirts: "Hello KhozaLand.\n\nI would like a quotation for custom T-shirt printing.\n\nQuantity:\nDesign requirements:\nRequired date:",
  uniforms: "Hello KhozaLand.\n\nI would like a quotation for branded uniforms.\n\nProduct:\nQuantity:\nLogo requirements:",
  cultural: "Hello KhozaLand.\n\nI would like to enquire about an Ingcawe/Ngcawe blanket or Ibhayi lombhaco.\n\nPreferred product:\nPersonalisation:\nQuantity:",
  corporate: "Hello KhozaLand.\n\nI would like a quotation for corporate gifts.\n\nProduct:\nQuantity:\nBranding requirements:",
  bulk: "Hello KhozaLand.\n\nI would like to enquire about a bulk custom order.\n\nOrganisation:\nProduct:\nQuantity:\nRequired date:",
  gifts: "Hello KhozaLand.\n\nI would like a quotation for a personalised gift.\n\nProduct:\nPersonalisation (names / photo / message):\nQuantity:",
  printing: "Hello KhozaLand.\n\nI would like a quotation for printing.\n\nItem (banner / stickers / vinyl):\nQuantity:\nDesign requirements:",
};

export const CATEGORIES = [
  { value: "custom-clothing", label: "Custom T-Shirts & Branded Clothing" },
  { value: "uniforms-embroidery", label: "Uniforms & Embroidery" },
  { value: "printing-branding", label: "Printing & Branding" },
  { value: "xhosa-cultural", label: "Xhosa Cultural Products" },
  { value: "corporate-gifts", label: "Corporate Gifts & Promotional Products" },
  { value: "personalised-gifts", label: "Personalised Gifts" },
  { value: "banners-stickers", label: "Banners, Stickers & Promotional Printing" },
  { value: "bulk-group", label: "Bulk & Group Orders" },
  { value: "other", label: "Other / Not sure yet" },
];

export const APPAREL_CATEGORIES = ["custom-clothing", "uniforms-embroidery", "bulk-group"];

export const NAV = [
  { label: "Home", to: "/" },
  {
    label: "Our Services",
    to: "/printing-branding",
    children: [
      { label: "Printing & Branding", to: "/printing-branding", desc: "DTF, heat transfer, vinyl & embroidery" },
      { label: "Uniforms & Embroidery", to: "/uniforms-embroidery", desc: "Business, school & group uniforms" },
      { label: "Banners, Stickers & Printing", to: "/banners-stickers", desc: "Promotional printing" },
      { label: "Bulk & Group Orders", to: "/bulk-orders", desc: "Churches, schools, societies & teams" },
    ],
  },
  {
    label: "Products",
    to: "/custom-tshirt-printing",
    children: [
      { label: "Custom T-Shirts & Clothing", to: "/custom-tshirt-printing", desc: "T-shirts, golf shirts, jackets" },
      { label: "Corporate Gifts", to: "/corporate-gifts", desc: "Branded mugs, tumblers & more" },
      { label: "Personalised Gifts", to: "/personalised-gifts", desc: "Mugs, cushions, candles" },
    ],
  },
  { label: "Xhosa Cultural Products", to: "/xhosa-cultural-products" },
  { label: "Our Work", to: "/gallery" },
  { label: "About Us", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const FOOTER_LINKS = [
  {
    title: "Services",
    links: [
      { label: "Printing & Branding", to: "/printing-branding" },
      { label: "Uniforms & Embroidery", to: "/uniforms-embroidery" },
      { label: "Banners & Stickers", to: "/banners-stickers" },
      { label: "Bulk & Group Orders", to: "/bulk-orders" },
    ],
  },
  {
    title: "Products",
    links: [
      { label: "Custom T-Shirts & Clothing", to: "/custom-tshirt-printing" },
      { label: "Corporate Gifts", to: "/corporate-gifts" },
      { label: "Personalised Gifts", to: "/personalised-gifts" },
      { label: "Xhosa Cultural Products", to: "/xhosa-cultural-products" },
    ],
  },
  {
    title: "KhozaLand",
    links: [
      { label: "Our Work", to: "/gallery" },
      { label: "About Us", to: "/about" },
      { label: "FAQs", to: "/faqs" },
      { label: "Request a Quote", to: "/contact" },
    ],
  },
];
